import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/utils/api';
import type { UnifiedContentItem } from '@/types';

const UNIFIED_FEED_KEY = 'unified-feed';

interface UnifiedFeedParams {
  category?: string;
  content_type?: 'all' | 'tweets' | 'rss';
  time_window_hours?: number;
  include_rss?: boolean;
}

interface UnifiedFeedResponse {
  items: UnifiedContentItem[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  content_breakdown: {
    tweets: number;
    rss: number;
  };
}

export function useUnifiedFeed(params: UnifiedFeedParams = {}) {
  return useQuery<UnifiedFeedResponse>({
    queryKey: [UNIFIED_FEED_KEY, params],
    queryFn: async () => {
      const response = await api.get('/tweets/unified-feed', {
        params: {
          category: params.category,
          content_type: params.content_type || 'all',
          time_window_hours: params.time_window_hours || 24,
          include_rss: params.include_rss !== false,
          page: 1,
          page_size: 20,
        },
      });
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function useInfiniteUnifiedFeed(params: UnifiedFeedParams = {}) {
  return useInfiniteQuery<UnifiedFeedResponse>({
    queryKey: [UNIFIED_FEED_KEY, 'infinite', params],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.get('/tweets/unified-feed', {
        params: {
          category: params.category,
          content_type: params.content_type || 'all',
          time_window_hours: params.time_window_hours || 24,
          include_rss: params.include_rss !== false,
          page: pageParam,
          page_size: 20,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.has_more) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });
}
